import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from "@capacitor/push-notifications";
import { FCM } from "@capacitor-community/fcm";
import { CustomerAPI } from "./customer_api.service";

// https://developmobileapplications.com/firebase-push-notification-in-ionic-5
// https://capacitorjs.com/docs/guides/push-notifications-firebase
// IOS : https://capacitorjs.com/docs/apis/push-notifications#ios
// IOS : APN: https://devdactic.com/push-notifications-ionic-capacitor/

// ANDROID:
// Download the google-services.json file to your local machine.
// Then move the file into your Capacitor Android project directory, specifically under android/app/.

@Injectable({
  providedIn: "root",
})
export class FcmService {
  private myToken: string | null = null;
  
  constructor(private api: CustomerAPI) {}
  
  /**
   * Get the current FCM token
   * Optional parameter kept for backward compatibility; it is ignored.
   * @returns The FCM token or null if not yet received
   */
  getToken(_existingToken?: any): string | null {
    return this.myToken;
  }
  
  /**
   * Check if token is available
   * @returns true if token exists, false otherwise
   */
  hasToken(): boolean {
    return this.myToken !== null && this.myToken !== undefined;
  }
  
  /**
   * Manually set the FCM token (used by older code paths)
   */
  setToken(token: string | null): void {
    this.myToken = token;
  }
  
  /**
   * Wait for token to be available (with timeout)
   * @param timeoutMs Maximum time to wait in milliseconds (default: 10000)
   * @returns Promise that resolves with token or null if timeout
   */
  async waitForToken(timeoutMs: number = 10000): Promise<string | null> {
    if (this.myToken) {
      return this.myToken;
    }
    
    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.myToken) {
          clearInterval(checkInterval);
          resolve(this.myToken);
        } else if (Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          console.warn(`[FCM] Timeout waiting for token after ${timeoutMs}ms`);
          resolve(null);
        }
      }, 100);
    });
  }

  // initPush() {
  //   if (Capacitor.platform !== 'web') {
  //     this.registerPush();
  //   }
  // }

  async logout() {
    const data = { token: this.myToken };
    // (await this.api.deviceTokenDeregister({ data })).subscribe((result2) => {
    //   const r: any = result2;
    //   if (!r.ok) return;
    // });
  }
  async initPush(): Promise<void> {
    console.log(`[FCM] Initializing push notifications on platform:`, Capacitor.getPlatform());
    
    if (Capacitor.getPlatform() === "web") {
      console.log(`[FCM] Web platform detected, skipping push notification initialization`);
      return;
    }

    try {
      // Check current permissions
      const permissionStatus = await PushNotifications.checkPermissions();
      console.log(`[FCM] Current permission status:`, permissionStatus);

      // Request permission to use push notifications
      // iOS will prompt user and return if they granted permission or not
      // Android will just grant without prompting
      const permissionResult = await PushNotifications.requestPermissions();
      console.log(`[FCM] Permission request result:`, permissionResult);

      if (permissionResult.receive === "granted") {
        console.log(`[FCM] Permissions granted, registering for push notifications...`);
        // Register with Apple / Google to receive push via APNS/FCM
        await PushNotifications.register();
        console.log(`[FCM] Registration request sent successfully`);
      } else {
        console.error(`[FCM] Permission denied. Status:`, permissionResult.receive);
        return;
      }
    } catch (error) {
      console.error(`[FCM] Error during permission/registration:`, error);
      return;
    }

    // On success, we should be able to receive notifications
    PushNotifications.addListener("registration", async (token: Token) => {
      console.log(`[FCM] Registration listener triggered. Token received:`, token.value);
      console.log(`[FCM] Platform:`, Capacitor.getPlatform());
      
      try {
        if (Capacitor.getPlatform() === "ios") {
          console.log(`[FCM] iOS detected, getting FCM token...`);
          const fcmResult = await FCM.getToken();
          this.myToken = fcmResult.token;
          console.log(`[FCM] iOS FCM token received:`, this.myToken);
          
          const data = {
            platform: Capacitor.getPlatform(),
            token: this.myToken,
          };
          
          const result2 = await (await this.api.deviceTokenRegister({ data })).toPromise();
          const r: any = result2;
          if (r.ok) {
            console.log(`[FCM] iOS token registered successfully with backend`);
          } else {
            console.error(`[FCM] Failed to register iOS token with backend:`, r);
          }
        } else {
          // Android
          this.myToken = token.value;
          console.log(`[FCM] Android token received:`, this.myToken);
          
          const data = {
            tokenRaw: token,
            platform: Capacitor.getPlatform(),
            token: this.myToken,
          };
          
          const result = await (await this.api.deviceTokenRegister({ data })).toPromise();
          const r: any = result;
          if (r.ok) {
            console.log(`[FCM] Android token registered successfully with backend`);
          } else {
            console.error(`[FCM] Failed to register Android token with backend:`, r);
          }
        }
      } catch (error) {
        console.error(`[FCM] Error processing token:`, error);
      }
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener("registrationError", (error: any) => {
      console.error(`[FCM] Registration error occurred:`, error);
      console.error(`[FCM] Error details:`, JSON.stringify(error, null, 2));
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotificationSchema) => {
        console.log(`[FCM] Push notification received:`, notification);
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (notification: ActionPerformed) => {
        console.log(`[FCM] Push notification action performed:`, notification);
      }
    );
    
    console.log(`[FCM] All listeners registered successfully`);
  }
}
